/**
 * FRONT-1739: whether the open reader is hand-editable (the header pencil)
 * and what EDDY is told it may rewrite (the `assetEditable` field on the
 * chat request) are ONE decision, computed here once — never two
 * independent expressions. This is the repo's "a disabled control and its
 * explanation are one decision" rule (FRONT-1670, CLAUDE.md) applied to a
 * capability instead of a tooltip: naming the editable/non-editable cases
 * individually in two places is exactly how such a pair drifts.
 *
 * Mattias's explicit decision: EDDY's "change it for me" must cover EXACTLY
 * what the pencil covers — Eddy's own markdown documents, and uploaded
 * plain-TEXT files (`canEditUpload`), never PDF, Word, Excel, image or
 * binary uploads.
 *
 * Pure: takes the reader's already-resolved inputs (no queries, no DOM), so
 * `inbox-chat-modal.tsx`'s two call sites — the pencil's `hide` prop and the
 * `assetEditable` opt threaded into `useAgentChat` — read the SAME result
 * and can never disagree. The caller (`inbox-chat-modal.tsx`) is also the
 * single source for the "live-preferred" values (`liveFileKind`,
 * `liveFileName`, `documentTitle`) — this module reads exactly what it's
 * given and does not re-derive a second fallback on top, so there is only
 * ever ONE place (the caller) that decides "live value or stub".
 *
 * The wire contract (`assetEditable: { kind, name, title? }`) is fixed by
 * the backend (`src/agents/asset-context-note.ts`) — `name` must be the
 * exact value the corresponding store resolves an in-place write against:
 * `memory_documents.filename` for a document, `user_files.name` for a plain
 * file (`getFileByName`, src/files/store.ts). Verified against the running
 * local stack (FRONT-1739): the Files list response, the file-detail
 * response and the document-detail response all carry that same value under
 * `name`/`filename` respectively — no display-only alias to substitute.
 * `title` (document kind only) pins the document's CURRENT title so the
 * backend can instruct the model literally rather than leaving it to
 * paraphrase — see asset-context-note.ts's own docblock for why that
 * matters (an invented title silently renames the document on overwrite).
 */
import { canEditUpload, type FileAssetItem } from './modal-asset';

export type AssetEditableKind = 'document' | 'file';

export interface AssetEditablePayload {
  kind: AssetEditableKind;
  name: string;
  /** Document kind only — the document's current title, when known. See
   *  this module's docblock and asset-context-note.ts. */
  title?: string;
}

export interface EditableAssetInput {
  /** `feedItem?.type === 'document'`. */
  isDocument: boolean;
  /** `feedItem?.filename` — the document's primary key. Only read when
   *  `isDocument` is true. */
  documentFilename: string | undefined;
  /** The document's CURRENTLY DISPLAYED title — the same value the reader's
   *  own header renders (`resolveDisplayTitle` in modal-asset.ts, which
   *  already folds in a same-session rename). Only read when `isDocument`
   *  is true. Optional on the wire: a caller that can't resolve a title yet
   *  (still loading) simply omits it, and the backend degrades safely. */
  documentTitle: string | undefined;
  /** The open upload, if the reader is showing one — `null` for a document,
   *  a chat thread, or a cron-run feed item. */
  fileAsset: Pick<FileAssetItem, 'name' | 'fileKind'> | null;
  /** The live-preferred kind (`fileData?.kind ?? fileAsset?.fileKind`),
   *  already resolved by the caller. FRONT-1453: a session reopened from a
   *  bare id can start from a stub whose kind is a placeholder guess until
   *  the live detail query lands — only the live value may prove the file
   *  is (or isn't) plain text. Only read when `fileAsset` is present. */
  liveFileKind: FileAssetItem['fileKind'] | undefined;
  /** The live-preferred name (`fileData?.name ?? fileAsset?.name`), already
   *  resolved by the caller — same reasoning as `liveFileKind`. Only read
   *  when `fileAsset` is present. */
  liveFileName: string | undefined;
}

export interface EditableAssetResult {
  /** Gates the header pencil and `startEdit` — never read independently of
   *  `assetEditable` below; see this module's docblock. */
  canEdit: boolean;
  /** The `assetEditable` field for the chat request body, or `undefined`
   *  when the open item isn't hand-editable. */
  assetEditable: AssetEditablePayload | undefined;
}

export function resolveEditableAsset(input: EditableAssetInput): EditableAssetResult {
  if (input.isDocument) {
    const name = input.documentFilename;
    if (!name) return { canEdit: false, assetEditable: undefined };
    const title = input.documentTitle?.trim();
    return {
      canEdit: true,
      assetEditable: { kind: 'document', name, ...(title ? { title } : {}) },
    };
  }
  if (input.fileAsset) {
    if (!input.liveFileKind || !canEditUpload(input.liveFileKind)) {
      return { canEdit: false, assetEditable: undefined };
    }
    const name = input.liveFileName;
    if (!name) return { canEdit: false, assetEditable: undefined };
    return { canEdit: true, assetEditable: { kind: 'file', name } };
  }
  return { canEdit: false, assetEditable: undefined };
}
